import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import Icon from '../../../../components/common/Icon';
import PianoScalePopup from '../../../../components/PianoScale/PianoScalePopup';
import { normalizeKeyDisplay } from '../../../../utils/diatonicChords';
import { calculateSemitones } from '../../../../utils/autoTranspose';
import { getSetting, settingKeys } from '../../../../utils/settings';
import useYouTubeAvailability from '../../../../hooks/useYouTubeAvailability';
import { extractYouTubeVideoId } from '../../../../utils/youtube';
import PracticeVideoModal from '../../../../components/modals/PracticeVideoModal';
import { ReactComponent as YouTubeIcon } from '../../../../icons/youtube.svg';
import { ReactComponent as ClockIcon } from '../../../../icons/clock.svg';
import { ReactComponent as UndoIcon } from '../../../../icons/undo.svg';

import './SongDetails.scss';

/**
 * The card under a song's bar: key, tempo, time signature and video as a row of blocks. Every song
 * in the view gets this one, the first and each one after it in continuous view.
 */
const SongDetails = ({ song, songKey, resetAutoTranspose }) => {
  const { t } = useTranslation();
  const [pianoAnchorRect, setPianoAnchorRect] = useState(null);
  const preferSharps = getSetting(settingKeys.preferSharps) !== false;
  const youtubeUnavailable = useYouTubeAvailability(song.youtube);
  const [practicing, setPracticing] = useState(false);
  const videoId = extractYouTubeVideoId(song.youtube);

  // Prefer the offset tracked on the song (manual + auto transpose accumulate it).
  // Fall back to deriving it from the keys for any song that predates that tracking.
  const transposeOffset =
    typeof song.autoTransposeSemitones === 'number'
      ? song.autoTransposeSemitones
      : calculateSemitones(song.originalKey, songKey);
  // Shown as the shortest way round from the original key, so +12 is no change and +7 reads -5
  const shownOffset = ((((transposeOffset + 5) % 12) + 12) % 12) - 5;
  const showReset = song.isAutoTransposed && song.originalKey && shownOffset !== 0;

  // Opens the practice player in place. Cmd/Ctrl/Shift-click, a middle click, or a video YouTube says
  // is gone still follow the link as before
  const handleYouTubeClick = (e) => {
    if (!videoId || youtubeUnavailable || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    setPracticing(true);
  };

  const handleKeyClick = (e) => {
    if (pianoAnchorRect) {
      setPianoAnchorRect(null);
    } else {
      setPianoAnchorRect(e.currentTarget.getBoundingClientRect());
    }
  };

  const handlePianoClose = useCallback(() => setPianoAnchorRect(null), []);

  const handleResetToOriginalKey = () => {
    if (song.isAutoTransposed) {
      resetAutoTranspose?.(song._id);
    }
  };

  return (
    <div className="SongDetails">
      <div className="card">
        <div className="stats">
          <div className={cx('stat key-stat', { transposed: showReset })}>
            <div className="key-col">
              <span className="stat-value">
                {/* TransposePlugin reads the key off .song-key's text */}
                <span className="song-key clickable-link" onClick={handleKeyClick}>
                  {normalizeKeyDisplay(songKey, preferSharps)}
                </span>
              </span>
              <span className="stat-label">{t('view.key')}</span>
            </div>
            {showReset && (
              <button
                type="button"
                className="reset-key-btn"
                onClick={handleResetToOriginalKey}
                aria-label={t('view.backToOriginalKey', { key: normalizeKeyDisplay(song.originalKey, preferSharps) })}
              >
                <Icon icon={<UndoIcon />} />
                <span className="original-key">
                  {normalizeKeyDisplay(song.originalKey, preferSharps)}
                  <span className="offset">
                    {shownOffset > 0 ? '+' : '−'}
                    {Math.abs(shownOffset)}
                  </span>
                </span>
              </button>
            )}
            <PianoScalePopup currentKey={songKey} anchorRect={pianoAnchorRect} onClose={handlePianoClose} />
          </div>
          {song.bpm && (
            <div className="stat">
              <span className="stat-value">{song.bpm}</span>
              <span className="stat-label">BPM</span>
            </div>
          )}
          <div className="stat">
            <span className="stat-value">{song.timeSignature || '4/4'}</span>
            <span className="stat-label" title={t('view.timeSignature')}>
              <Icon icon={<ClockIcon />} />
            </span>
          </div>
          {song.youtube && (
            <a
              className={cx('stat youtube', { unavailable: youtubeUnavailable })}
              href={song.youtube}
              target="blank"
              title={youtubeUnavailable ? t('view.videoUnavailable') : 'YouTube'}
              aria-label="YouTube"
              onClick={handleYouTubeClick}
            >
              <Icon icon={<YouTubeIcon />} />
            </a>
          )}
          {practicing && (
            <PracticeVideoModal videoId={videoId} youtubeUrl={song.youtube} onClose={() => setPracticing(false)} />
          )}
        </div>
      </div>
    </div>
  );
};

export default SongDetails;
